import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react"
import { useT } from "../i18n/index.tsx"
import { Icon, type IconName } from "./icons.tsx"

/**
 * 라벨·필수표시·힌트·오류를 한 곳에서 다룬다.
 *
 * 입력칸과 문구를 잇는 `aria-describedby` 는 호출자가 붙인다 — `htmlFor` 를 주면
 * 문구 요소의 id 가 `${htmlFor}-msg` 가 되므로 그 값을 쓰면 된다.
 */
export function Field({
  label,
  htmlFor,
  required = false,
  hint,
  error,
  wide = false,
  rowStart = false,
  children,
}: {
  label: ReactNode
  htmlFor?: string
  required?: boolean
  hint?: ReactNode
  error?: ReactNode
  /** 폼 격자에서 한 줄을 다 쓴다(비고처럼 긴 값). */
  wide?: boolean
  /** 폼 격자에서 항상 새 줄 첫 칸으로 내려간다(앞 칸과 묶이지 않게). */
  rowStart?: boolean
  children: ReactNode
}) {
  const t = useT()
  const message = error ?? hint
  const classes = ["field"]
  if (wide) classes.push("field--wide")
  if (rowStart) classes.push("field--row-start")
  return (
    <div className={classes.join(" ")}>
      <label className="field__label" htmlFor={htmlFor}>
        {label}
        {required ? (
          <>
            <span className="field__req" aria-hidden="true">
              *
            </span>
            <span className="sr-only">{t("common.required")}</span>
          </>
        ) : null}
      </label>
      {children}
      {message ? (
        <span
          className={`field__msg field__msg--${error ? "error" : "hint"}`}
          id={htmlFor ? `${htmlFor}-msg` : undefined}
        >
          {message}
        </span>
      ) : null}
    </div>
  )
}

export function Input({
  invalid = false,
  size = "md",
  icon,
  className,
  ...rest
}: {
  invalid?: boolean
  size?: "md" | "lg"
  /** 왼쪽에 붙는 아이콘(검색칸 등). */
  icon?: IconName
  // React 19 는 함수 컴포넌트에서도 ref 를 보통 prop 으로 넘긴다(forwardRef 불필요).
  ref?: Ref<HTMLInputElement>
  // HTML 의 size 는 "글자 수"라 숫자다. 우리 size 와 이름이 겹치므로 그쪽을 뺀다.
} & Omit<InputHTMLAttributes<HTMLInputElement>, "size">) {
  const classes = ["input", `input--${size}`]
  if (icon) classes.push("input--with-icon")
  if (invalid) classes.push("is-invalid")
  if (className) classes.push(className)

  const input = (
    <input className={classes.join(" ")} aria-invalid={invalid || undefined} {...rest} />
  )
  if (!icon) return input

  return (
    <span className="input-wrap">
      <Icon name={icon} size={16} className="input-wrap__icon" />
      {input}
    </span>
  )
}

export function Select({
  invalid = false,
  className,
  children,
  ...rest
}: { invalid?: boolean } & SelectHTMLAttributes<HTMLSelectElement>) {
  const classes = ["select"]
  if (invalid) classes.push("is-invalid")
  if (className) classes.push(className)
  return (
    <select className={classes.join(" ")} aria-invalid={invalid || undefined} {...rest}>
      {children}
    </select>
  )
}

export function Textarea({
  invalid = false,
  className,
  ...rest
}: { invalid?: boolean } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const classes = ["textarea"]
  if (invalid) classes.push("is-invalid")
  if (className) classes.push(className)
  return <textarea className={classes.join(" ")} aria-invalid={invalid || undefined} {...rest} />
}

/** 넓으면 여러 열, 좁으면 1열로 접히는 폼 격자. */
export function FormGrid({
  children,
  columns = 2,
}: {
  children: ReactNode
  columns?: 1 | 2 | 3
}) {
  return <div className={`form-grid form-grid--${columns}`}>{children}</div>
}
